import {
  ANYONE_CAN,
  ANYONE_CAN_DO_ANYTHING,
  type ExpressionBuilder,
  type PermissionsConfig,
} from "@rocicorp/zero";
import { definePermissions } from "@rocicorp/zero";

import { type Schema, schema } from "./schema.generated";

type AuthData = {
  sub: string;
};

export const permissions = definePermissions<AuthData, Schema>(schema, () => {
  const allowIfRoomOwner = (
    authData: AuthData,
    { cmp }: ExpressionBuilder<Schema, "room">,
  ) => cmp("owner", authData.sub);

  const allowIfDocumentOwner = (
    authData: AuthData,
    { cmp }: ExpressionBuilder<Schema, "document">,
  ) => cmp("owner", authData.sub);

  const allowIfBlockIsPartOfOwnedDocument = (
    authData: AuthData,
    { eb }: ExpressionBuilder<Schema, "block">,
  ) => eb.exists("document", (q) => q.where("owner", authData.sub));

  return {
    room: {
      row: {
        select: ANYONE_CAN,
        insert: [allowIfRoomOwner],
        delete: [allowIfRoomOwner],
        update: {
          preMutation: [allowIfRoomOwner],
          postMutation: [allowIfRoomOwner],
        },
      },
    },
    user: {
      row: {
        // There is no way to select a subset of columns to be returned by ZQL,
        // this means we leak user email addresses with this permission.
        select: ANYONE_CAN,
      },
    },
    document: {
      row: {
        select: ANYONE_CAN,
        insert: [allowIfDocumentOwner],
        delete: [allowIfDocumentOwner],
        update: {
          preMutation: [allowIfDocumentOwner],
          postMutation: [allowIfDocumentOwner],
        },
      },
    },
    block: {
      row: {
        select: ANYONE_CAN,
        insert: [allowIfBlockIsPartOfOwnedDocument],
        delete: [allowIfBlockIsPartOfOwnedDocument],
        update: {
          preMutation: [allowIfBlockIsPartOfOwnedDocument],
          postMutation: [allowIfBlockIsPartOfOwnedDocument],
        },
      },
    },
  } satisfies PermissionsConfig<AuthData, Schema>;
});
